import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Briefcase, Camera, KeyRound, Lock, Mail, MessageSquare, Trash2, UserRound } from "lucide-react";
import UserAvatar from "../../components/ui/UserAvatar";
import AlertNotification from "../../components/notification";
import { usePageTitle } from "../../components/layouts/PageTitleContext";
import { useChat } from "../../components/chat/ChatProvider";
import { formatLastSeen } from "../../components/chat/chatUtils";
import AvatarCropModal from "./AvatarCropModal";
import {
  changeMyPassword,
  fetchMyProfile,
  fetchProfile,
  removeMyAvatar,
  removeUserAvatar,
  updateMyProfile,
  uploadMyAvatar,
} from "../../controllers/profileController";

const MAX_SOURCE_IMAGE_SIZE = 20 * 1024 * 1024;
const PHOTO_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"];
const LABEL = "mb-1 block text-sm font-medium text-app-secondary";
const INPUT =
  "w-full rounded-lg border border-app-primary bg-app-input px-3 py-2 text-sm text-app-primary placeholder:text-app-muted outline-none transition-colors focus:border-[#2470B7] focus:ring-2 focus:ring-blue-100 dark:focus:border-blue-400 dark:focus:ring-blue-500/20";
const PRIMARY_BUTTON =
  "inline-flex items-center justify-center gap-2 rounded-lg border border-[#2470B7] bg-[#2470B7] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#1f5f9b] disabled:cursor-not-allowed disabled:opacity-60 dark:border-blue-600 dark:bg-blue-600 dark:hover:bg-blue-500";
const SECONDARY_BUTTON =
  "inline-flex items-center justify-center gap-2 rounded-lg border border-app-primary px-4 py-2 text-sm font-semibold text-app-secondary transition hover:bg-app-hover hover:text-app-primary disabled:opacity-60";

const errorText = (err, fallback) => err?.response?.data?.message || err?.response?.data?.error || fallback;
const humanize = (value) => (value ? value.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase()) : null);
const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString([], { year: "numeric", month: "long", day: "numeric" }) : null;

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-app-muted">{label}</p>
      <p className="mt-0.5 break-words text-sm text-app-primary">{value || "—"}</p>
    </div>
  );
}

function Card({ title, icon: Icon, children, action }) {
  return (
    <section className="rounded-xl border border-app-primary bg-app-card shadow-sm dark:shadow-none">
      <div className="flex items-center justify-between gap-3 border-b border-app-primary px-6 py-4">
        <h2 className="flex items-center gap-2 text-base font-semibold text-app-heading">
          {Icon && <Icon className="h-4 w-4 text-app-muted" aria-hidden="true" />}
          {title}
        </h2>
        {action}
      </div>
      <div className="px-6 py-5">{children}</div>
    </section>
  );
}

function AboutSection({ profile, onSaved, notify }) {
  const editable = profile.editable;
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const startEditing = () => {
    setForm({
      bio: profile.bio || "",
      firstName: profile.firstName || "",
      lastName: profile.lastName || "",
      jobTitle: profile.jobTitle || "",
    });
    setError("");
    setEditing(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    const changes = { bio: form.bio };
    if (editable.name) Object.assign(changes, { firstName: form.firstName, lastName: form.lastName });
    if (editable.jobTitle) changes.jobTitle = form.jobTitle;
    try {
      onSaved(await updateMyProfile(changes));
      setEditing(false);
      notify("Profile updated.", true);
    } catch (err) {
      setError(errorText(err, "Couldn't save your profile."));
    } finally {
      setSaving(false);
    }
  };

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  return (
    <Card
      title="About"
      icon={UserRound}
      action={
        editable && !editing ? (
          <button type="button" onClick={startEditing} className={SECONDARY_BUTTON} data-tour="profile-edit">
            Edit
          </button>
        ) : null
      }
    >
      {editing ? (
        <form onSubmit={save} className="space-y-4">
          {error && <p className="rounded-lg border border-app-error bg-app-error px-3 py-2 text-sm text-app-error">{error}</p>}
          {editable.name && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="pf-first" className={LABEL}>First name</label>
                <input id="pf-first" maxLength={100} value={form.firstName} onChange={set("firstName")} className={INPUT} />
              </div>
              <div>
                <label htmlFor="pf-last" className={LABEL}>Last name</label>
                <input id="pf-last" maxLength={100} value={form.lastName} onChange={set("lastName")} className={INPUT} />
              </div>
            </div>
          )}
          {editable.jobTitle && (
            <div>
              <label htmlFor="pf-title" className={LABEL}>Job title</label>
              <input id="pf-title" maxLength={150} value={form.jobTitle} onChange={set("jobTitle")} className={INPUT} placeholder="e.g. Research Analyst" />
            </div>
          )}
          <div>
            <label htmlFor="pf-bio" className={LABEL}>Bio</label>
            <textarea id="pf-bio" rows={4} maxLength={1000} value={form.bio} onChange={set("bio")} className={`${INPUT} resize-y`} placeholder="A few words about you and your work" />
            <p className="mt-1 text-right text-xs text-app-muted">{form.bio.length}/1000</p>
          </div>
          {profile.linked && (
            <p className="text-xs text-app-muted">Your name, job title and department come from your HR employee record. Ask HR to change them.</p>
          )}
          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => setEditing(false)} className={SECONDARY_BUTTON} disabled={saving}>Cancel</button>
            <button type="submit" className={PRIMARY_BUTTON} disabled={saving}>{saving ? "Saving…" : "Save"}</button>
          </div>
        </form>
      ) : (
        <div className="space-y-5">
          <p className="whitespace-pre-wrap text-sm text-app-primary">
            {profile.bio || <span className="text-app-muted">{profile.isSelf ? "Add a short bio so colleagues know what you work on." : "No bio yet."}</span>}
          </p>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <Field label="Email" value={profile.email} />
            <Field label="Job title" value={profile.jobTitle} />
            <Field label="Department" value={profile.department} />
            {!profile.restricted && <Field label="Member since" value={formatDate(profile.memberSince)} />}
          </div>
        </div>
      )}
    </Card>
  );
}

function EmploymentSection({ profile, canOpenHrRecord }) {
  const e = profile.employee;
  if (!e) {
    return (
      <Card title="Employment" icon={Briefcase}>
        <p className="text-sm text-app-muted">
          {profile.isSelf
            ? "Your account isn't linked to an HR employee record yet. HR can link it from the HR module."
            : "This account isn't linked to an HR employee record."}
        </p>
      </Card>
    );
  }
  return (
    <Card
      title="Employment"
      icon={Briefcase}
      action={
        canOpenHrRecord ? (
          <Link to={`/hr/employees/${e.id}`} className={SECONDARY_BUTTON}>Open HR record</Link>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs text-app-muted"><Lock className="h-3.5 w-3.5" /> Managed by HR</span>
        )
      }
    >
      <div className="grid grid-cols-2 gap-5 sm:grid-cols-3">
        <Field label="Employee code" value={e.employeeCode} />
        <Field label="Designation" value={e.designation} />
        <Field label="Department" value={e.department} />
        <Field label="Location" value={e.location ? [e.location.name, e.location.city].filter(Boolean).join(", ") : null} />
        <Field label="Joining date" value={formatDate(e.joiningDate)} />
        <Field label="Employment type" value={humanize(e.employmentType)} />
        <Field label="Work mode" value={humanize(e.workMode)} />
        <Field label="Status" value={humanize(e.status)} />
        <Field label="Work email" value={e.email} />
        <Field label="Phone" value={e.phone} />
        <Field label="CNIC" value={e.cnic} />
        <Field label="Address" value={e.address} />
      </div>
      <p className="mt-5 flex items-center gap-1.5 text-xs text-app-muted">
        <Lock className="h-3.5 w-3.5" /> Only you, HR and admins can see these details.
      </p>
    </Card>
  );
}

function SecuritySection({ notify }) {
  const empty = { currentPassword: "", newPassword: "", confirmPassword: "" };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.newPassword !== form.confirmPassword) return setError("New passwords do not match.");
    setSaving(true);
    setError("");
    try {
      await changeMyPassword(form);
      setForm(empty);
      notify("Password changed.", true);
    } catch (err) {
      setError(errorText(err, "Couldn't change your password."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card title="Change password" icon={KeyRound}>
      <form onSubmit={submit} className="max-w-md space-y-4" data-tour="profile-password">
        {error && <p className="rounded-lg border border-app-error bg-app-error px-3 py-2 text-sm text-app-error">{error}</p>}
        <div>
          <label htmlFor="pw-current" className={LABEL}>Current password</label>
          <input id="pw-current" type="password" autoComplete="current-password" required value={form.currentPassword} onChange={set("currentPassword")} className={INPUT} />
        </div>
        <div>
          <label htmlFor="pw-new" className={LABEL}>New password</label>
          <input id="pw-new" type="password" autoComplete="new-password" required value={form.newPassword} onChange={set("newPassword")} className={INPUT} />
          <p className="mt-1 text-xs text-app-muted">At least 8 characters, with an uppercase letter, a lowercase letter, a number and a special character.</p>
        </div>
        <div>
          <label htmlFor="pw-confirm" className={LABEL}>Confirm new password</label>
          <input id="pw-confirm" type="password" autoComplete="new-password" required value={form.confirmPassword} onChange={set("confirmPassword")} className={INPUT} />
        </div>
        <button type="submit" className={PRIMARY_BUTTON} disabled={saving}>{saving ? "Changing…" : "Change password"}</button>
      </form>
    </Card>
  );
}

// Profile of the signed-in user (/profile) or of anyone else (/profile/:userId).
function ProfilePage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const chat = useChat();
  const [profile, setProfile] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [tab, setTab] = useState("about");
  const [notification, setNotification] = useState(null);
  const [cropFile, setCropFile] = useState(null);
  const [upload, setUpload] = useState({ saving: false, progress: 0, error: "" });
  const fileInputRef = useRef(null);

  const notify = useCallback((text, status) => setNotification({ text, status }), []);

  const load = useCallback(async () => {
    setLoadError("");
    try {
      setProfile(await (userId ? fetchProfile(userId) : fetchMyProfile()));
    } catch (err) {
      setProfile(null);
      setLoadError(err?.response?.status === 404 ? "This user doesn't exist." : "Couldn't load this profile.");
    }
  }, [userId]);

  useEffect(() => {
    setTab("about");
    load();
  }, [load]);

  // Your own profile lives at /profile.
  useEffect(() => {
    if (userId && profile?.isSelf) navigate("/profile", { replace: true });
  }, [userId, profile, navigate]);

  // Live name/photo/presence from the chat connection.
  const live = profile ? chat?.users?.[profile.id] : null;
  const view = profile && live ? { ...profile, name: live.name, avatarUrl: live.avatarUrl, avatarThumbUrl: live.avatarThumbUrl, jobTitle: live.jobTitle, department: live.department, online: live.online, lastSeenAt: live.lastSeenAt } : profile;

  usePageTitle(view ? (view.isSelf ? "My Profile" : view.name) : null);

  const pickPhoto = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!PHOTO_TYPES.includes(file.type)) return notify("Choose a PNG, JPEG, WebP or GIF image.", false);
    if (file.size > MAX_SOURCE_IMAGE_SIZE) return notify("That image is too large (20 MB max).", false);
    setUpload({ saving: false, progress: 0, error: "" });
    setCropFile(file);
  };

  const savePhoto = async (blob) => {
    setUpload({ saving: true, progress: 0, error: "" });
    try {
      setProfile(await uploadMyAvatar(blob, (progress) => setUpload((u) => ({ ...u, progress }))));
      setCropFile(null);
      setUpload({ saving: false, progress: 0, error: "" });
      notify("Profile photo updated.", true);
    } catch (err) {
      setUpload({ saving: false, progress: 0, error: errorText(err, "Upload failed. Try again.") });
    }
  };

  const removePhoto = async () => {
    try {
      setProfile(view.isSelf ? await removeMyAvatar() : await removeUserAvatar(view.id));
      notify("Profile photo removed.", true);
    } catch (err) {
      notify(errorText(err, "Couldn't remove the photo."), false);
    }
  };

  if (loadError) return <div className="flex justify-center py-16 text-app-muted">{loadError}</div>;
  if (!view) return <div className="flex justify-center py-16 text-app-muted">Loading…</div>;

  const can = view.permissions || {};
  const tabs = [
    { id: "about", label: "About", icon: UserRound },
    ...(!view.restricted ? [{ id: "employment", label: "Employment", icon: Briefcase }] : []),
    ...(view.isSelf ? [{ id: "security", label: "Security", icon: KeyRound }] : []),
  ];

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <section className="overflow-hidden rounded-xl border border-app-primary bg-app-card shadow-sm dark:shadow-none">
        <div className="h-28 bg-gradient-to-r from-[#2470B7] via-[#1d5a93] to-[#002776]" />
        <div className="flex flex-col gap-4 px-6 pb-6 sm:flex-row sm:items-end">
          <div className="relative -mt-14 w-fit" data-tour="profile-photo">
            <UserAvatar name={view.name} src={view.avatarUrl} seed={view.id} size="xl" online={view.online} className="rounded-full ring-4 ring-white dark:ring-gray-900" />
            {view.isSelf && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="absolute bottom-1 left-1 flex h-9 w-9 items-center justify-center rounded-full border-2 border-white bg-[#2470B7] text-white shadow-md hover:bg-[#1f5f9b] dark:border-gray-900"
                title="Change photo"
                aria-label="Change profile photo"
              >
                <Camera className="h-4 w-4" />
              </button>
            )}
            <input ref={fileInputRef} type="file" accept={PHOTO_TYPES.join(",")} className="hidden" onChange={pickPhoto} />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-2xl font-semibold text-app-heading">{view.name}</h1>
            {(view.jobTitle || view.department) && (
              <p className="truncate text-sm text-app-secondary">{[view.jobTitle, view.department].filter(Boolean).join(" · ")}</p>
            )}
            <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-app-muted">
              <span className="inline-flex items-center gap-1.5"><Mail className="h-4 w-4" />{view.email}</span>
              <span className="inline-flex items-center gap-1.5">
                <span className={`h-2 w-2 rounded-full ${view.online ? "bg-green-500" : "bg-gray-400"}`} />
                {formatLastSeen(view)}
              </span>
              {view.linked && view.employee?.employeeCode && (
                <span className="rounded-full border border-app-primary px-2 py-0.5 text-xs">{view.employee.employeeCode}</span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {!view.isSelf && chat && (
              <button type="button" onClick={() => chat.startDirectChat(view.id)} className={PRIMARY_BUTTON} data-tour="profile-message">
                <MessageSquare className="h-4 w-4" /> Message
              </button>
            )}
            {view.avatarUrl && can.removeAvatar && (
              <button type="button" onClick={removePhoto} className={SECONDARY_BUTTON}>
                <Trash2 className="h-4 w-4" /> Remove photo
              </button>
            )}
          </div>
        </div>
      </section>

      {tabs.length > 1 && (
        <div className="flex gap-1 border-b border-app-primary" role="tablist" data-tour="profile-tabs">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              onClick={() => setTab(id)}
              className={`-mb-px inline-flex items-center gap-2 border-b-2 px-4 py-2 text-sm font-semibold transition ${
                tab === id ? "border-[#2470B7] text-[#2470B7] dark:border-blue-400 dark:text-blue-400" : "border-transparent text-app-muted hover:border-app-primary hover:text-app-primary"
              }`}
            >
              <Icon className="h-4 w-4" aria-hidden="true" /> {label}
            </button>
          ))}
        </div>
      )}

      {tab === "about" && <AboutSection profile={view} onSaved={setProfile} notify={notify} />}
      {tab === "employment" && <EmploymentSection profile={view} canOpenHrRecord={!!can.openHrRecord} />}
      {tab === "security" && <SecuritySection notify={notify} />}

      {cropFile && (
        <AvatarCropModal
          file={cropFile}
          onCancel={() => setCropFile(null)}
          onSave={savePhoto}
          saving={upload.saving}
          progress={upload.progress}
          error={upload.error}
        />
      )}
      {notification && <AlertNotification text={notification.text} status={notification.status} onClose={() => setNotification(null)} />}
    </div>
  );
}

export default ProfilePage;
